import { configureHttpClient } from '@mathflat/domain/http/client'
import { parentApi } from '@mathflat/domain/Parent'
import { configureApiBoundary } from '@mathflat/domain/schema/apiBoundary'
import { type Student, studentApi } from '@mathflat/domain/Student'
import { showApiBoundaryIssue } from '@mathflat/shared/@common/apiBoundaryOverlay'
import { readBridgeToken } from '@mathflat/vine-auth/bridge-token'

import { strapi } from '~/@common/api/strapiApi'
import { routeName } from '~/@common/constants'
import { handleStudentMainLogic } from '~/@common/integration/handleStudentMainLogic'
import { handleStudentUpLogic } from '~/@common/integration/handleStudentUpLogic'
import { migrateFromHashParam } from '~/@common/integration/migrateFromHashParam'
import { isIntegrationBridgePath } from '~/@common/integration/routeGuards'
import { resolveSafeRedirectURI } from '~/@common/integration/safeRedirect'
import authService from '~/@common/services/auth.service'
import { nativeEventBus } from '~/@common/services/nativeEventBus'
import { commonRepo } from '~/@common/services/repo.service'
import { localStorageService } from '~/@common/services/storage.service'
import chatAxios from '~/@common/utils/chatAxios'
import { domainHttpClient } from '~/@common/utils/domainHttpClient'
import { envUtils } from '~/@common/utils/envUtils'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import { writeLoginRole } from '~/@common/utils/loginRoleCookie'
import noxios from '~/@common/utils/noxios'
import { remoteStorageAxios } from '~/@common/utils/remoteStorageAxios'
import { toDomainHttpClient } from '~/@common/utils/toDomainHttpClient'

import { initAxiosDefaultBaseURL } from './envSetting/envSettings'
import { loadInitialData } from './loadInitialData'
import { initPolyfill } from './shims'

export const initialize = async () => {
  configureHttpClient(domainHttpClient)
  configureHttpClient(toDomainHttpClient(chatAxios), 'ai')
  configureHttpClient(toDomainHttpClient(strapi), 'cms')
  configureHttpClient(toDomainHttpClient(remoteStorageAxios), 'remoteStorage')
  configureHttpClient(toDomainHttpClient(noxios), 'messaging')

  configureApiBoundary({
    // 어긋난 응답에 예외를 던지면 화면 전체가 흰 화면이 되므로 어느 환경에서도 던지지 않는다.
    strict: false,
    reporter: (issue) => {
      // e2e 에서는 오버레이가 클릭을 가로막으므로 띄우지 않는다.
      if (!envUtils.isLive && !envUtils.isStaging && !import.meta.env.VITE_E2E_DEV_SERVER) {
        showApiBoundaryIssue(issue)
      }
    },
  })

  initPolyfill()

  // 네이티브 이벤트 버스 초기화: 모든 네이티브 이벤트에 대해 window listener 등록.
  // ready()는 useApp에서 모든 구독 완료 후 별도 호출한다.
  // React 마운트 전 도착한 이벤트는 eventBuffer에 누적되었다가
  // subscribe() 시점에 즉시 재전달된다.
  nativeEventBus.initialize()

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      nativeEventBus.dispose()
    })
  }

  if (envUtils.isTestEnv) {
    initAxiosDefaultBaseURL(localStorage.getItem('DEV_API') ?? 'dev')
  }

  const url = new URL(window.location.toString())
  const searchParams = url.searchParams
  const searchParamsSize = [...url.searchParams.keys()].length

  // hash가 있으면 외부에서 토큰을 받아올 것이므로 jwtTokenService.initializeAuth() 건너뜀
  const hasHashParam = searchParamsSize > 0 && searchParams.has('hash')

  authService.prepareJwtAuth()

  // 1. receiver(student) 역할: sender(student-up) /integration/sync iframe 에 storage sync 를 요청한다.
  const mainResult = await handleStudentMainLogic()
  if (mainResult.action !== 'continue') return // SYNC_SUCCESS → reload 진행

  // 2. sender(student-up) 역할: receiver 배포 probe 결과를 보고 student 로 redirect 한다.
  const upResult = await handleStudentUpLogic()
  if (upResult.action !== 'continue') return // student-up → student redirect

  try {
    await authService.initializeJwtAuth({ skipInitializeAuth: hasHashParam })
  } catch (error) {
    // ★ fail-safe — storage 보존, reason redirect 만.
    window.location.replace(
      `${localStorageService.loginPath ?? routeName.login}?reason=auth_init_failed`,
    )
    return
  }

  /**
   * 통합(student-up) -> 통합(student)로 넘어왔을때 hash 파라미터 처리
   * -> student-up 에서 세션스토리지 로그인해서 사용하던 사용자를 새로고침 시 student로 넘겨주기 위함
   * (참고: 세션스토리지는 독립 존재하므로 아이프레임띄워서 가져올 수 없음)
   */
  if (hasHashParam) {
    // ★ iframe 안에선 hash 처리 스킵 — migrateFromHashParam 의 setTokens('LOGIN') broadcast 가
    //   same-origin top-level frame 의 onTokenReceived 발화 → reload trigger → 무한 루프 방지.
    //   probe iframe 안에 베이직(student)이 student-up 으로 navigate 한 경우 (origin 변경) 발생.
    if (window.self !== window.top) {
      return
    }
    try {
      await migrateFromHashParam(searchParams)
      const cleanUrl =
        window.location.protocol + '//' + window.location.host + window.location.pathname
      window.history.replaceState({ path: cleanUrl }, '', cleanUrl)
    } catch (error) {
      // ★ fail-safe — storage 보존, reason redirect 만.
      window.location.replace(
        `${localStorageService.loginPath ?? routeName.login}?reason=hash_migration_failed`,
      )
    }
    return
  }

  if (window.location.pathname === routeName.bridge.teacherIframe) {
    return
  }

  // 토큰을 들고 브릿지로 들어왔으면 그 계정으로 들어가겠다는 뜻이다. 기존 세션으로 초기 데이터를
  // 먼저 불러오면 브릿지가 로그인하기도 전에 이전 계정 화면이 뜬다.
  if (
    window.location.pathname === routeName.loginVineBridge &&
    readBridgeToken(window.location.search)
  ) {
    return
  }

  if (authService.isLoggedIn) {
    // 로그인 상태에서 로그인 진입 페이지 진입 시 기본 페이지로 리다이렉트
    // redirectURI가 있으면 해당 페이지로 이동 (ProtectedRoute가 비로그인 시 전달한 원래 경로)
    if (
      window.location.pathname === routeName.login ||
      window.location.pathname === routeName.loginCreverse ||
      window.location.pathname === routeName.loginVine
    ) {
      const searchParams = new URLSearchParams(window.location.search)
      const safeRedirect = resolveSafeRedirectURI(searchParams.get('redirectURI'))
      window.location.replace(safeRedirect ?? getEntrancePage(authService.userType))
      return
    }

    // QR 판단 경로는 쿠키를 읽는 쪽이라, 여기서 먼저 쓰면 선생님 기록을 읽기 직전에 덮는다.
    if (authService.userType === 'STUDENT' && !isIntegrationBridgePath(window.location.pathname)) {
      writeLoginRole('student')
    }

    try {
      const { studentId } = localStorageService.user

      if (studentId) {
        await loadInitialData(studentId)
      }

      // PARENT 새로고침 시 childStudents 복원
      if (commonRepo.permission.isParent) {
        const { parentId } = localStorageService.user
        if (parentId) {
          const { studentIds } = await parentApi.getParentChildren(parentId)
          const students = await studentApi.getStudentsByIds(studentIds as Student['id'][])
          commonRepo.parent.setChildStudents(
            students.map((s) => ({ studentId: s.id, name: s.name ?? '' })),
          )
          if (studentId) {
            commonRepo.parent.setActiveChildStudentId(studentId)
          }
        }
      }
    } catch (err) {
      // 예외는 maxios에서 처리함
    }

    return
  }

  authService.requestToken()
}
